import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Settings, Plus, Edit, Save, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface A4FModel {
  id: string;
  model_name: string;
  display_name: string;
  provider_number: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AdminA4FModelManagement = () => {
  const [models, setModels] = useState<A4FModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<A4FModel>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newModel, setNewModel] = useState<Partial<A4FModel>>({
    model_name: '',
    display_name: '',
    provider_number: 2,
    is_active: false
  });

  useEffect(() => {
    fetchA4FModels();
  }, []);

  const fetchA4FModels = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await (supabase as any)
        .from('a4f_models')
        .select('*')
        .order('provider_number');

      if (error) {
        console.error('Error fetching prompt enhancement models:', error);
        throw error;
      }
      
      setModels(data || []);
    } catch (error) {
      console.error('Error fetching prompt enhancement models:', error);
      toast.error('Error loading prompt enhancement models');
    } finally {
      setLoading(false);
    }
  };

  const handleEditModel = (model: A4FModel) => {
    setEditingModel(model.id);
    setEditForm({
      model_name: model.model_name,
      display_name: model.display_name,
      provider_number: model.provider_number,
      is_active: model.is_active
    });
  };

  const handleSaveModel = async () => {
    if (!editingModel || !editForm) return;
    
    try {
      const { error } = await (supabase as any)
        .from('a4f_models')
        .update({
          model_name: editForm.model_name,
          display_name: editForm.display_name,
          provider_number: editForm.provider_number,
          is_active: editForm.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingModel);

      if (error) throw error;

      toast.success('Model updated successfully');
      setEditingModel(null);
      setEditForm({});
      fetchA4FModels();
    } catch (error) {
      console.error('Error updating model:', error);
      toast.error('Error updating model');
    }
  };

  const handleCancelEdit = () => {
    setEditingModel(null);
    setEditForm({});
  };

  const handleAddModel = async () => {
    if (!newModel.model_name || !newModel.display_name || !newModel.provider_number) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const { error } = await (supabase as any)
        .from('a4f_models')
        .insert([{
          model_name: newModel.model_name,
          display_name: newModel.display_name,
          provider_number: newModel.provider_number,
          is_active: newModel.is_active || false
        }]);

      if (error) throw error;

      toast.success('Model added successfully');
      setNewModel({
        model_name: '',
        display_name: '',
        provider_number: 2,
        is_active: false
      });
      setShowAddForm(false);
      fetchA4FModels();
    } catch (error) {
      console.error('Error adding model:', error);
      toast.error('Error adding model');
    }
  };

  const handleDeleteModel = async (id: string) => {
    const confirmed = confirm('Are you sure you want to delete this model?');
    if (confirmed) {
      try {
        const { error } = await (supabase as any)
          .from('a4f_models')
          .delete()
          .eq('id', id);

        if (error) throw error;

        toast.success('Model deleted successfully');
        fetchA4FModels();
      } catch (error) {
        console.error('Error deleting model:', error);
        toast.error('Error deleting model');
      }
    }
  };

  const toggleModelStatus = async (id: string, currentStatus: boolean) => {
    try {
      // If activating this model, first deactivate all others
      if (!currentStatus) {
        await (supabase as any)
          .from('a4f_models')
          .update({ is_active: false })
          .neq('id', id);
      }

      const { error } = await (supabase as any)
        .from('a4f_models')
        .update({ 
          is_active: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Model ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchA4FModels();
    } catch (error) {
      console.error('Error updating model status:', error);
      toast.error('Error updating model status');
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Settings className="w-8 h-8 mx-auto mb-4 animate-pulse text-blue-400" />
            <p className="text-white">Loading prompt enhancement models...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-white flex items-center">
            <Settings className="w-6 h-6 mr-2" />
            Prompt Enhancement Models ({models.length} models)
          </CardTitle>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Model
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Add Model Form */}
        {showAddForm && (
          <div className="mb-6 p-4 bg-white/10 rounded-lg border border-white/20">
            <h3 className="text-white font-medium mb-4">Add New Prompt Enhancement Model</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Model Name (e.g., gpt-4.1-2025-04-14)"
                value={newModel.model_name || ''}
                onChange={(e) => setNewModel({...newModel, model_name: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                placeholder="Display Name"
                value={newModel.display_name || ''}
                onChange={(e) => setNewModel({...newModel, display_name: e.target.value})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Input
                type="number"
                placeholder="Provider Number"
                value={newModel.provider_number || ''}
                onChange={(e) => setNewModel({...newModel, provider_number: parseInt(e.target.value) || 2})}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleAddModel} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                Save Model
              </Button>
              <Button onClick={() => setShowAddForm(false)} className="bg-gray-600 hover:bg-gray-700">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Models Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-gray-300">Model</TableHead>
                <TableHead className="text-gray-300">Provider</TableHead>
                <TableHead className="text-gray-300">Status</TableHead>
                <TableHead className="text-gray-300">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {models
                .sort((a, b) => a.provider_number - b.provider_number)
                .map((model) => (
                <TableRow key={model.id} className="border-white/10">
                  <TableCell>
                    {editingModel === model.id ? (
                      <div className="space-y-2">
                        <Input
                          value={editForm.model_name || ''}
                          onChange={(e) => setEditForm({...editForm, model_name: e.target.value})}
                          className="bg-white/10 border-white/20 text-white text-sm"
                          placeholder="Model name"
                        />
                        <Input
                          value={editForm.display_name || ''}
                          onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                          className="bg-white/10 border-white/20 text-white text-sm"
                          placeholder="Display name"
                        />
                      </div>
                    ) : (
                      <div>
                        <p className="text-white font-medium">{model.display_name}</p>
                        <p className="text-gray-400 text-sm">{model.model_name}</p>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingModel === model.id ? (
                      <Input
                        type="number"
                        value={editForm.provider_number || ''}
                        onChange={(e) => setEditForm({...editForm, provider_number: parseInt(e.target.value) || 2})}
                        className="w-20 bg-white/10 border-white/20 text-white"
                      />
                    ) : (
                      <div className="text-gray-400 text-sm">
                        Provider {model.provider_number}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={model.is_active ? 'bg-green-600/20 text-green-300' : 'bg-red-600/20 text-red-300'}>
                      {model.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {editingModel === model.id ? (
                        <>
                          <Button
                            size="sm"
                            onClick={handleSaveModel}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleCancelEdit}
                            className="bg-gray-600 hover:bg-gray-700"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleEditModel(model)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => toggleModelStatus(model.id, model.is_active)}
                            className={model.is_active ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
                          >
                            {model.is_active ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleDeleteModel(model.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        {models.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400">No prompt enhancement models configured.</p>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-6 p-4 bg-white/10 rounded-lg border border-white/20">
          <h3 className="text-white font-medium mb-2">Important Notes</h3>
          <p className="text-gray-300 text-sm mb-2">
            • Only one model can be active at a time for prompt enhancement
          </p>
          <p className="text-gray-300 text-sm">
            • Enabling a model will automatically disable all others
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminA4FModelManagement;